import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { Loader2, Download, TrendingUp, Calendar, Info, RotateCcw, Pencil } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_YEARS = [2027, 2028];
const QUICK_PCTS = [5, 7, 10];

const BarBaseInput = ({ room, onSave, onReset }) => {
  const [value, setValue] = useState(String(room.bar_base));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(String(room.bar_base)); }, [room.bar_base]);

  const commit = async () => {
    const n = Number(value);
    if (!value || Number.isNaN(n) || n === room.bar_base) { setValue(String(room.bar_base)); return; }
    if (n < 20 || n > 2000) { toast.error("La BAR base debe estar entre €20 y €2000"); setValue(String(room.bar_base)); return; }
    setSaving(true);
    await onSave(room.name, n);
    setSaving(false);
  };

  const slug = room.name.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
  return (
    <div className="flex items-center justify-end gap-1.5">
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
        <input
          type="number"
          inputMode="numeric"
          min={20}
          max={2000}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          disabled={saving}
          data-testid={`bar-input-${slug}`}
          className={`w-24 pl-5 pr-2 py-1 rounded-md border text-right font-bold text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            room.manual ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200 text-emerald-700"
          }`}
        />
      </div>
      {room.manual ? (
        <button
          type="button"
          onClick={() => onReset(room.name)}
          title={`Volver al cálculo automático (€${room.computed_bar_base})`}
          data-testid={`bar-reset-${slug}`}
          className="p-1 rounded text-amber-700 hover:bg-amber-100"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      ) : (
        <Pencil className="w-3.5 h-3.5 text-slate-300" />
      )}
    </div>
  );
};

const FutureRates = () => {
  const [year, setYear] = useState(2027);
  const [pct, setPct] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchRates = useCallback(async (y, p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/future-rates?year=${y}&pct=${p}`, { headers });
      setData(res.data);
    } catch {
      toast.error("Error cargando tarifas");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchRates(year, pct);
  }, [year, pct, fetchRates]);

  const saveOverride = async (room, barBase) => {
    try {
      await axios.put(`${API}/api/future-rates/override`, { year, room, bar_base: barBase }, { headers });
      toast.success(`${room}: BAR base €${Math.round(barBase)} guardada`);
      await fetchRates(year, pct);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando la BAR base");
    }
  };

  const resetOverride = async (room) => {
    try {
      await axios.put(`${API}/api/future-rates/override`, { year, room, bar_base: null }, { headers });
      toast.success(`${room}: vuelve al cálculo por %`);
      await fetchRates(year, pct);
    } catch {
      toast.error("Error restableciendo la BAR base");
    }
  };

  const resetAll = async () => {
    try {
      await axios.delete(`${API}/api/future-rates/overrides?year=${year}`, { headers });
      toast.success(`Tarifas ${year} restablecidas al cálculo por %`);
      await fetchRates(year, pct);
    } catch {
      toast.error("Error restableciendo las tarifas");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/api/future-rates/pdf?year=${year}&pct=${pct}`,
        { headers, responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Tarifas_${year}_+${pct}pct_PortSitges.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF Tarifas ${year} +${pct}% descargado`);
    } catch {
      toast.error("Error descargando PDF");
    } finally {
      setDownloading(false);
    }
  };

  const pctColor = pct >= 10 ? "text-rose-600" : pct >= 7 ? "text-amber-600" : "text-emerald-600";

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto" data-testid="future-rates-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-blue-600" />
            Tarifas Futuras
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Genera tarifas 2027 y 2028 con subida compuesta desde 2026. Elige año + %.
          </p>
        </div>
        <Button
          data-testid="download-future-pdf-btn"
          onClick={downloadPdf}
          disabled={downloading || loading}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Descargar PDF {year} +{pct}%
        </Button>
      </div>

      {/* Controls */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Year selector */}
            <div>
              <label className="text-xs uppercase font-bold text-slate-500 tracking-wide flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Año
              </label>
              <div className="flex gap-2 mt-2">
                {SUPPORTED_YEARS.map((y) => (
                  <Button
                    key={y}
                    data-testid={`year-btn-${y}`}
                    variant={year === y ? "default" : "outline"}
                    onClick={() => setYear(y)}
                    className={year === y ? "bg-blue-600 hover:bg-blue-700" : ""}
                  >
                    {y}
                  </Button>
                ))}
              </div>
            </div>

            {/* Pct display */}
            <div>
              <label className="text-xs uppercase font-bold text-slate-500 tracking-wide">
                Subida compuesta anual
              </label>
              <div className={`text-4xl font-bold mt-1 ${pctColor}`} data-testid="pct-display">
                +{pct}%
              </div>
              {data && (
                <div className="text-xs text-slate-500">
                  Factor total ×{data.compound_factor} ({data.years_compounded} año{data.years_compounded > 1 ? "s" : ""} compuestos)
                </div>
              )}
            </div>
          </div>

          {/* Quick presets */}
          <div>
            <label className="text-xs uppercase font-bold text-slate-500 tracking-wide">Rápido</label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {QUICK_PCTS.map((p) => (
                <Button
                  key={p}
                  data-testid={`pct-preset-${p}`}
                  size="sm"
                  variant={pct === p ? "default" : "outline"}
                  onClick={() => setPct(p)}
                  className={pct === p ? "bg-blue-600 hover:bg-blue-700" : ""}
                >
                  +{p}%
                </Button>
              ))}
            </div>
          </div>

          {/* Slider */}
          <div>
            <label className="text-xs uppercase font-bold text-slate-500 tracking-wide">
              Fino ({pct}%)
            </label>
            <Slider
              data-testid="pct-slider"
              value={[pct]}
              onValueChange={(v) => setPct(v[0])}
              min={0}
              max={20}
              step={1}
              className="mt-2"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>0%</span><span>10%</span><span>20%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : data ? (
        <>
          {/* BAR base */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="w-2 h-6 rounded bg-emerald-500 inline-block" />
                    Precios BAR base — para copiar en Oscar
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    Precio de referencia (Jul L-J) por tipología. Puedes teclear la BAR base a mano: la tabla por temporada se ajusta en proporción.
                    {data.manual_count > 0 && (
                      <span className="ml-1 text-amber-700 font-semibold" data-testid="manual-count">
                        · {data.manual_count} manual{data.manual_count > 1 ? "es" : ""}
                      </span>
                    )}
                  </p>
                </div>
                {data.manual_count > 0 && (
                  <Button variant="outline" size="sm" onClick={resetAll} data-testid="reset-all-overrides-btn">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Volver al cálculo por %
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-200 bg-slate-50">
                      <th className="text-left py-2 px-3 font-semibold">Tipología</th>
                      <th className="text-center py-2 px-3 font-semibold">Uds.</th>
                      <th className="text-right py-2 px-3 font-semibold">BAR base</th>
                      <th className="text-right py-2 px-3 font-semibold">Mínimo</th>
                      <th className="text-right py-2 px-3 font-semibold">Máximo</th>
                    </tr>
                  </thead>
                  <tbody data-testid="bar-base-table">
                    {data.rooms.map((r) => (
                      <tr key={r.name} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3">
                          {r.name}
                          {r.manual && (
                            <span className="ml-2 text-[10px] uppercase font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                              manual · auto €{r.computed_bar_base}
                            </span>
                          )}
                        </td>
                        <td className="text-center py-2 px-3 text-slate-500">{r.qty}</td>
                        <td className="text-right py-2 px-3">
                          <BarBaseInput room={r} onSave={saveOverride} onReset={resetOverride} />
                        </td>
                        <td className="text-right py-2 px-3 text-slate-500">€{r.seasonal.min}</td>
                        <td className="text-right py-2 px-3 text-slate-500">€{r.seasonal.max}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Seasonal table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="w-2 h-6 rounded bg-blue-500 inline-block" />
                Tabla por temporada — {year}
              </CardTitle>
              <p className="text-xs text-slate-500">L-J = Lunes-Jueves · V-S = Viernes-Sábado</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b-2 bg-blue-600 text-white">
                      <th className="text-left py-2 px-2 font-semibold sticky left-0 bg-blue-600">Tipología</th>
                      {["Jul L-J","Jul V-S","Ago L-J","Ago V-S","Sep L-J","Sep V-S","Oct L-J","Oct V-S","Nov L-J","Nov V-S","Dic L-J","Dic V-S","Navidad","Fin Año"].map((h) => (
                        <th key={h} className="text-center py-2 px-1 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody data-testid="seasonal-table">
                    {data.rooms.map((r, i) => (
                      <tr key={r.name} className={`border-b ${i % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                        <td className="py-2 px-2 font-medium sticky left-0 bg-inherit whitespace-nowrap">{r.name}</td>
                        <td className="text-center py-2 px-1 font-mono">€{r.seasonal.jul_lj}</td>
                        <td className="text-center py-2 px-1 font-mono font-bold">€{r.seasonal.jul_vs}</td>
                        <td className="text-center py-2 px-1 font-mono">€{r.seasonal.ago_lj}</td>
                        <td className="text-center py-2 px-1 font-mono font-bold">€{r.seasonal.ago_vs}</td>
                        <td className="text-center py-2 px-1 font-mono">€{r.seasonal.sep_lj}</td>
                        <td className="text-center py-2 px-1 font-mono font-bold">€{r.seasonal.sep_vs}</td>
                        <td className="text-center py-2 px-1 font-mono">€{r.seasonal.oct_lj}</td>
                        <td className="text-center py-2 px-1 font-mono font-bold">€{r.seasonal.oct_vs}</td>
                        <td className="text-center py-2 px-1 font-mono">€{r.seasonal.nov_lj}</td>
                        <td className="text-center py-2 px-1 font-mono font-bold">€{r.seasonal.nov_vs}</td>
                        <td className="text-center py-2 px-1 font-mono">€{r.seasonal.dic_lj}</td>
                        <td className="text-center py-2 px-1 font-mono font-bold">€{r.seasonal.dic_vs}</td>
                        <td className="text-center py-2 px-1 font-mono text-rose-600 font-bold">€{r.seasonal.navidad}</td>
                        <td className="text-center py-2 px-1 font-mono text-rose-600 font-bold">€{r.seasonal.fin_año}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 flex gap-3 items-start">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <b>Cómo aplicarlo en Oscar:</b> descarga el PDF y copia los precios BAR base en
                <i> Oscar → Tarifas → Precios base</i>. Las reglas de revenue automático que ya
                tienes activas (±%) se aplicarán encima de estos nuevos precios sin cambios.
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default FutureRates;
